<template>
  <div>
    <template v-if="!(isEdit && detailContactStore.loading) && !detailContactStore.error">
      <v-container>
        <v-form v-model="formValid" :ref="form">

          <v-radio-group
            v-model="contact.Anrede"
            :rules="[ruleRequired]"
            inline
            label="Anrede *"
          >
            <v-radio label="Herr" value="Herr"></v-radio>
            <v-radio label="Frau" value="Frau"></v-radio>
          </v-radio-group>
          <v-text-field
            v-model="contact.Vorname"
            label="Vorname"
            :rules="[ruleMaxLength(30)]"
          />
          <v-text-field
            v-model="contact.Nachname"
            label="Nachname *"
            :rules="[ruleRequired, ruleMaxLength(30)]"
          />
          <v-text-field
            v-model="contact.Position"
            label="Position"
            :rules="[ruleMaxLength(30)]"
          />
          <v-text-field
            v-model="contact.Abteilung"
            label="Abteilung"
            :rules="[ruleMaxLength(30)]"
          />
          <v-text-field
            v-model="contact.AufgabenGebiet"
            label="Aufgabengebiet"
            :rules="[ruleMaxLength(30)]"
          />
          <date-picker
            class="mt-5"
            v-model="austrittsDatum"
            label="Austrittsdatum"
          />
          <v-text-field
            v-model="contact.Strasse"
            label="Strasse"
            :rules="[ruleMaxLength(42)]"
          />
          <v-text-field
            v-model="contact.Plz"
            label="PLZ"
            :rules="[ruleMaxLength(10)]"
          />
          <v-text-field
            v-model="contact.Ort"
            label="Ort"
            :rules="[ruleMaxLength(42)]"
          />
          <v-text-field
            v-model="contact.Telefon1"
            label="Telefon"
            :rules="[ruleMaxLength(20)]"
          />
          <v-text-field
            v-model="contact.Telefon2"
            label="Mobil"
            :rules="[ruleMaxLength(20)]"
          />
          <v-text-field
            v-model="contact.Email"
            label="E-Mail"
            :rules="[ruleEmailValid, ruleMaxLength(255)]"
          />
          <v-text-field v-model="contact.Homepage" label="Homepage" :rules="[ruleMaxLength(60)]" />
          <v-text-field
            v-model="contact.Info1"
            label="Info1"
            :rules="[ruleMaxLength(30)]"
          />
          <v-text-field
            v-model="contact.Info2"
            label="Info2"
            :rules="[ruleMaxLength(30)]"
          />
          <v-btn color="primary" @click="save" :disabled="!formValid" :loading="loadingSave">Speichern</v-btn>
        </v-form>
      </v-container>
    </template>
    <template v-else-if="detailContactStore.error">
      <error :module="detailContactStore.$id" />
    </template>
    <v-overlay :model-value="loadingSave || (isEdit && detailContactStore.loading)" class="align-center justify-center" persistent>
      <v-card>
        <v-card-text class="text-center">
          <div class="mb-4">
            <v-progress-circular indeterminate />
          </div>
          <div v-if="isEdit && detailContactStore.loading" class="mt-4">Ansprech&shy;partner&shy;erfassung wird geladen</div>
          <div v-if="loadingSave" class="mt-4">Ansprech&shy;partner wird gespeichert</div>
        </v-card-text>
      </v-card>
    </v-overlay>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, ref, watch} from 'vue';
import router from '@/router';
import {doAlert} from '@/util';
import Contact, {ApiContactSubmitData} from '@/model/Contact';
import {postContact, putContact} from '@/api/module/contact';
import DatePicker from '@/components/DatePicker.vue';
import useDetailContacts from '@/store/detailContact';
import {useRoute} from 'vue-router';
import {VNodeRef} from 'vue';

const route = useRoute();
const detailContactStore = useDetailContacts();
const loadingSave = ref(false);
const isEdit = route.params.contact_id !== undefined;
const contact = ref<ApiContactSubmitData>({
  Nachname: '',
  Vorname: null,
  Position: '',
  Abteilung: '',
  AufgabenGebiet: '',
  AustrittDatum: '',
  Anrede: '',
  Titel: null,
  Briefanrede: '',
  Name1: '',
  Name2: null,
  Name3: '',
  Strasse: '',
  NAT: 'D',
  Plz: '',
  Ort: '',
  Postfach: null,
  PostfachPlz: null,
  PostfachOrt: null,
  Telefon1: '',
  Telefon2: '',
  Telefax: '',
  Email: null,
  Homepage: '',
  Info1: '',
  Info2: '',
});
const formValid = ref(false);
const form = ref<HTMLFormElement & VNodeRef | undefined>();

async function save() {
  try {
    loadingSave.value = true;
    form.value?.validate();
    if (!formValid.value) {
      throw new Error('Bitte alle Felder korrekt ausfüllen');
    }
    contact.value.Vorname = contact.value.Vorname ? contact.value.Vorname : null;
    contact.value.Email = contact.value.Email ? contact.value.Email : null;
    const nameData = [contact.value.Nachname];
    if (contact.value.Vorname) nameData.push(contact.value.Vorname);
    contact.value.Name1 = nameData.join(', ');
    contact.value.AustrittDatum = contact.value.AustrittDatum ? contact.value.AustrittDatum : null;
    contact.value.Briefanrede = `Sehr geehrte${
          contact.value.Anrede === 'Herr' ? 'r' : ''
    } ${contact.value.Anrede} ${contact.value.Nachname}`;
    contact.value.Info1 = contact.value.Info1 ? contact.value.Info1 : null;
    contact.value.Info2 = contact.value.Info2 ? contact.value.Info2 : null;
    let contactFromApi;
    if (isEdit) {
      contactFromApi = await putContact(Number(route.params.id), Number(route.params.contact_id), contact.value);
    } else {
      contactFromApi = await postContact(Number(route.params.id), [contact.value]);
    }
    await detailContactStore.replace([contactFromApi, 1]);
    router.back();
  } catch (err: any) {
    await doAlert(err.message, 'Fehler');
  } finally {
    loadingSave.value = false;
  }
}
function ruleRequired(val: string | null | undefined): string | true {
  if (!val) return 'erforderlich';

  return true;
}
function ruleEmailValid(val: string | null | undefined): string | true {
  return (
    !val || /^[a-z0-9!#$%&'*+/=?^_'{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_'{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(val) ||
        'E-Mail muss gültig sein'
  );
}
function rulePhoneNumber(val: string | null | undefined): string | true {
  return (
    !val ||
        /^\+?[() /\-0-9]+$/.test(val) ||
        'Telefonnummer ungültig'
  );
}
function ruleMaxLength(maxLength: number): (val: string | null | number | undefined) => string | true {
  return (val) => {
    if (!val) return true;
    if (val.toString().length > maxLength) return `maximal ${maxLength} Zeichen`;

    return true;
  };
}

const austrittsDatum = computed({
  get(): string {
    return contact.value.AustrittDatum ?? '';
  },
  set(newDate: string | undefined) {
    if (!newDate) {
      contact.value.AustrittDatum = '';
      return;
    }
    contact.value.AustrittDatum = newDate;
  },
});

watch(() => detailContactStore.data, async (data: Contact | null) => {
  if (isEdit && data) {
    contact.value.Nachname = data.Nachname ?? '';
    contact.value.Vorname = data.Vorname ?? '';
    contact.value.Position = data.Position ?? '';
    contact.value.Abteilung = data.Abteilung ?? '';
    contact.value.AufgabenGebiet = data.AufgabenGebiet ?? '';
    contact.value.AustrittDatum = data.AustrittDatum?.slice(0, 10) ?? '';
    contact.value.Anrede = data.Anrede ?? '';
    contact.value.Briefanrede = data.Briefanrede ?? '';
    contact.value.Strasse = data.Strasse ?? '';
    contact.value.Plz = data.Plz ?? '';
    contact.value.Ort = data.Ort ?? '';
    contact.value.Telefon1 = data.Telefon1 ?? '';
    contact.value.Telefon2 = data.Telefon2 ?? '';
    contact.value.Email = data.Email ?? '';
    contact.value.Homepage = data.Homepage ?? '';
    contact.value.Info1 = data.Info1 ?? '';
    contact.value.Info2 = data.Info2 ?? '';
  }
  await nextTick();
  form.value?.validate();
}, {immediate: true});
watch(form, () => {
  form.value?.validate();
});

defineExpose({
  form,
  austrittsDatum,
  contact,
  detailContactStore,
  save,
  ruleEmailValid,
  ruleMaxLength,
  rulePhoneNumber,
  ruleRequired,
});
</script>
